import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  ExternalContainer,
  InternalContainer,
} from "../../components/Reusable/Layout";
import { GradientButton, OutlineButton } from "../../components/forms";
import {
  broadcastNotification,
  fetchNotificationHistory,
  fetchNotificationStats,
} from "../../features/auth/store/notificationSlice";
import {
  NotificationHistoryPagination,
  NotificationHistoryTable,
} from "./NotificationHistoryTable";

const TITLE_MAX = 100;
const BODY_MAX = 500;
const IMAGE_MAX_BYTES = 2 * 1024 * 1024; // 2MB, matches backend validation.

const Notifications = () => {
  const dispatch = useAppDispatch();
  const stats = useAppSelector((state: any) => state.notifications?.stats);
  const sending = useAppSelector(
    (state: any) => state.notifications?.sending ?? false,
  );
  const history = useAppSelector(
    (state: any) => state.notifications?.history ?? [],
  );
  const historyPagination = useAppSelector(
    (state: any) => state.notifications?.historyPagination,
  );
  const historyLoading = useAppSelector(
    (state: any) => state.notifications?.historyLoading ?? false,
  );

  const [tab, setTab] = useState<"compose" | "history">("compose");
  const [historyPage, setHistoryPage] = useState(1);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    dispatch(fetchNotificationStats());
  }, [dispatch]);

  useEffect(() => {
    if (tab === "history") {
      dispatch(fetchNotificationHistory(historyPage));
    }
  }, [dispatch, tab, historyPage]);

  // Revoke the object URL when the preview changes or the page unmounts.
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > IMAGE_MAX_BYTES) {
      toast.error("Image must be 2MB or smaller.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const canSend = title.trim().length > 0 && body.trim().length > 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;

    try {
      const result = await dispatch(
        broadcastNotification({
          title: title.trim(),
          body: body.trim(),
          data: url.trim() ? { url: url.trim() } : undefined,
          image,
        }),
      ).unwrap();

      toast.success(
        `Notification queued for ${result.recipients} device${
          result.recipients === 1 ? "" : "s"
        }.`,
      );
      setTitle("");
      setBody("");
      setUrl("");
      clearImage();
      setConfirming(false);
      dispatch(fetchNotificationStats());
    } catch (err: any) {
      toast.error(err?.message || String(err) || "Failed to send notification");
      setConfirming(false);
    }
  };

  const deviceCount = stats?.devices ?? 0;

  return (
    <ExternalContainer>
      <InternalContainer headerText="Push Notifications">
        <div className="inline-flex rounded-3xl border border-[#D0D5DD] dark:border-[#FFFFFF1A] overflow-hidden mb-6">
          {[
            { value: "compose", label: "Compose" },
            { value: "history", label: "History" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setTab(option.value as "compose" | "history")}
              className={`px-5 py-2 text-sm font-medium ${
                tab === option.value
                  ? "bg-[#027EC2] text-white"
                  : "bg-[#F4F4F5] text-gray-600 dark:bg-[#27272A] dark:text-white"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {tab === "history" ? (
          <div className="border border-[#FFFFFF1A] rounded-2xl">
            <NotificationHistoryTable
              notifications={history}
              loading={historyLoading}
            />
            {historyPagination && (
              <NotificationHistoryPagination
                pagination={historyPagination}
                loading={historyLoading}
                onPageChange={setHistoryPage}
              />
            )}
          </div>
        ) : (
          <>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              Sends a push notification to every device with the mobile app
              installed and notifications enabled.
              {stats && (
                <>
                  {" "}
                  Currently <strong>{deviceCount}</strong> registered device
                  {deviceCount === 1 ? "" : "s"} ({stats.ios} iOS,{" "}
                  {stats.android} Android).
                </>
              )}
            </p>

            <div className="flex flex-col gap-4">
              <label className="flex flex-col gap-2">
                <span className="satoshi-base-bold text-gray-700 dark:text-white">
                  Title
                </span>
                <input
                  type="text"
                  value={title}
                  maxLength={TITLE_MAX}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Weekend bonus is live"
                  className="rounded-lg border border-border-border1 dark:border-tabs bg-white dark:bg-BG-dark p-3 text-gray-700 dark:text-white outline-none"
                />
                <span className="text-xs text-gray-400">
                  {title.length}/{TITLE_MAX}
                </span>
              </label>

              <label className="flex flex-col gap-2">
                <span className="satoshi-base-bold text-gray-700 dark:text-white">
                  Message
                </span>
                <textarea
                  value={body}
                  rows={4}
                  maxLength={BODY_MAX}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Top up today and get 10% extra DG Coins."
                  className="rounded-lg border border-border-border1 dark:border-tabs bg-white dark:bg-BG-dark p-3 text-gray-700 dark:text-white outline-none resize-y"
                />
                <span className="text-xs text-gray-400">
                  {body.length}/{BODY_MAX}
                </span>
              </label>

              <label className="flex flex-col gap-2">
                <span className="satoshi-base-bold text-gray-700 dark:text-white">
                  Open this page on tap (optional)
                </span>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="/referrals"
                  className="rounded-lg border border-border-border1 dark:border-tabs bg-white dark:bg-BG-dark p-3 text-gray-700 dark:text-white outline-none"
                />
                <span className="text-xs text-gray-400">
                  An in-app path starting with “/”. Leave blank to just open
                  the app.
                </span>
              </label>

              <label className="flex flex-col gap-2">
                <span className="satoshi-base-bold text-gray-700 dark:text-white">
                  Picture (optional)
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={handleImageChange}
                  className="rounded-lg border border-border-border1 dark:border-tabs bg-white dark:bg-BG-dark p-3 text-gray-700 dark:text-white outline-none file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 dark:file:bg-tabs file:px-3 file:py-1 file:text-gray-700 dark:file:text-white"
                />
                <span className="text-xs text-gray-400">
                  JPG, PNG, GIF or WebP up to 2MB. Shown inside the
                  notification.
                </span>
                {imagePreview && (
                  <div className="mt-1 flex items-start gap-3">
                    <img
                      src={imagePreview}
                      alt="Notification preview"
                      className="h-24 w-24 rounded-lg object-cover border border-border-border1 dark:border-tabs"
                    />
                    <button
                      type="button"
                      onClick={clearImage}
                      className="text-sm text-red-500 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </label>

              {confirming ? (
                <div className="rounded-lg border border-amber-400 bg-amber-50 dark:bg-transparent p-4">
                  <p className="mb-4 text-sm text-gray-700 dark:text-white">
                    This sends “{title.trim()}” to all {deviceCount}{" "}
                    registered device{deviceCount === 1 ? "" : "s"}. This
                    cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <GradientButton
                      content={sending ? "Sending…" : "Yes, send to everyone"}
                      onClick={handleSend}
                      disabled={sending}
                    />
                    <OutlineButton
                      content="Cancel"
                      onClick={() => setConfirming(false)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex">
                  <GradientButton
                    content="Send notification"
                    onClick={() => setConfirming(true)}
                    disabled={!canSend}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </InternalContainer>
    </ExternalContainer>
  );
};

export default Notifications;
